import { FormattingRuleLabel } from './atoms';
import { APIEntityType, Operator } from '@ardoq/api-types';
import { AsyncSelect, Select } from '@ardoq/select';
import { GhostButton, DragButton } from '@ardoq/button';
import { DeleteIcon } from '@ardoq/icons';
import { GridFormattingRuleRow, InputsWrapper } from '../../../atoms';
import { ColorPickerWrapper } from './atoms';
import { FlexBox } from '@ardoq/layout';
import { TagFormattingRule } from '../types';
import { useFormattingContext } from '../useFormattingContext';
import { t } from '@ardoq/i18n';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import {
  NEW_DEFAULT_OPERATOR_TO_LABEL,
  RuleNotificationType,
} from '@ardoq/query-builder';
import { WarningNotificationRule } from '../../../WarningNotificationRule';
import { perspectivesRowDragAndDropHandlers } from '../../../perspectivesRowDragAndDropHandlers';
import { FormattingColorPicker } from '../../FormattingColorPicker';
import { buildColorWarningKey } from '../colorWarnings';
import { ValueWarningMessage } from './ValueWarningMessage';
import { FORMATTING_RULE_LINE_HEIGHT as LINE_HEIGHT } from '../../../consts';
import styled from 'styled-components';
import { FIELD_SELECTOR } from '@ardoq/forms';
import { s8 } from '@ardoq/design-tokens';
import { isRuleVersionSupported } from '../../../utils';

const GridInputsWrapper = styled(InputsWrapper)`
  display: block;
  min-width: 0;

  ${FIELD_SELECTOR} {
    flex: initial;
    min-width: 0;
    width: 100%;
  }
`;

const ColorSelectRow = styled.div`
  display: flex;
  gap: ${s8};
  align-items: start;
  min-width: 0;
`;

const SelectGrow = styled.div`
  flex: 1 1 0%;
  min-width: 0;
  width: 100%;

  ${FIELD_SELECTOR} {
    width: 100%;
    min-width: 0;
  }
`;

type NewFormattingRuleByTagProperties = {
  formattingRule: TagFormattingRule;
};
export const NewFormattingRuleRowByTag = ({
  formattingRule: rule,
}: NewFormattingRuleByTagProperties) => {
  const {
    updateRule,
    removeRule,
    asyncSuggestionsLoaders,
    tagsFormattingGroups,
    getRuleNotificationParams,
    ruleInfo,
    i18nLocale,
  } = useFormattingContext();
  const { __id: ruleId, isInverted } = rule;
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(
      rule.__id || null,
      useDragAndDropContext()
    );

  const { message, notificationType } = getRuleNotificationParams?.(rule) || {};
  const isWarning = notificationType === RuleNotificationType.WARNING;

  if (isWarning) {
    return (
      <WarningNotificationRule
        message={message}
        removeRule={() => removeRule(rule.__id)}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        dataTestId="group-box-formatting-warning-notification"
      />
    );
  }
  const { colorWarnings = {} } = ruleInfo[rule.__id] || {};
  const colorWarning = colorWarnings[buildColorWarningKey(rule.color)];

  const isDraggable = !!rule.__id;

  const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

  return (
    <GridFormattingRuleRow
      data-testid="group-box-formatting-rule-by-tag"
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
    >
      <DragButton
        draggable={isDraggable}
        style={{ lineHeight: `${LINE_HEIGHT}px` }}
        i18nLocale={i18nLocale}
      />
      <FormattingRuleLabel
        ruleType={APIEntityType.TAG}
        isDisabled={isRuleUnsupported}
      />
      <div>&nbsp;</div>
      <div>&nbsp;</div>

      <GridInputsWrapper $isDisabled={isRuleUnsupported}>
        <Select
          value={isInverted ? Operator.NOT_EQUAL : Operator.EQUAL}
          options={[
            {
              value: Operator.EQUAL,
              label: NEW_DEFAULT_OPERATOR_TO_LABEL[Operator.EQUAL],
            },
            {
              value: Operator.NOT_EQUAL,
              label: NEW_DEFAULT_OPERATOR_TO_LABEL[Operator.NOT_EQUAL],
            },
          ]}
          onChange={selectedOption => {
            if (!selectedOption) return;
            updateRule(ruleId, {
              isInverted: selectedOption.value === Operator.NOT_EQUAL,
            });
          }}
        />
      </GridInputsWrapper>
      <GridInputsWrapper $isDisabled={isRuleUnsupported}>
        <FlexBox flexDirection="column" gap="small">
          <FlexBox gap="small">
            <ColorSelectRow>
              <ColorPickerWrapper $hasWarning={Boolean(colorWarning)}>
                <FormattingColorPicker
                  dataTestId="tag-formatting-rule-color-picker"
                  value={rule.color}
                  onValueChange={color => updateRule(rule.__id, { color })}
                  isNewConditionalFormatting={true}
                />
              </ColorPickerWrapper>
            </ColorSelectRow>
            <SelectGrow>
              <AsyncSelect
                dataTestId="tag-formatting-rule-select"
                loadOptions={asyncSuggestionsLoaders.loadTagSuggestionsAsync}
                defaultOptions={tagsFormattingGroups}
                placeholder={t(
                  i18nLocale,
                  'perspectives:formattingRuleRow.searchAndSelect',
                  'Search and select'
                )}
                value={
                  rule.value ? { value: rule.value, label: rule.value } : null
                }
                onChange={selectedOption => {
                  if (!selectedOption) return;
                  updateRule(rule.__id, { value: selectedOption.value });
                }}
                hasWarning={Boolean(colorWarning)}
              />
            </SelectGrow>
          </FlexBox>
          <ValueWarningMessage message={colorWarning} />
        </FlexBox>
      </GridInputsWrapper>
      <GhostButton
        dataTestId="remove-tag-formatting-rule-button"
        onClick={() => removeRule(rule.__id)}
      >
        <DeleteIcon />
      </GhostButton>
    </GridFormattingRuleRow>
  );
};
